import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";
import { useCan } from "@/auth/permissions";
import { PageShell, Pill, EmptyState, LoadingState } from "@/shared/ui/PageHeader";
import { partnersApi, SETTLEMENT_MODE_LABEL, type SettlementMode } from "@/services/b2b";
import { openApiAdmin, type ApplicationStatus, type OpenApiApplication } from "@/services/openApi";
import { formatCents } from "@/services/finance";

/**
 * 开放平台审批（样稿 ⑪）：新同行接入申请 + 上线申请，都在这一页处理。
 *  - 接入申请：「审核」→ 弹窗（绑已有档案 / 新建 + 结算方式 + 授信额度 + 客群）→ 通过 / 拒绝（须填原因，发给对方）
 *  - 上线申请：清单全过的同行 → 「发正式密钥」（只显示一次，邮件通知对方）
 *  - 已处理：通过 / 拒绝的历史
 * 权限：看 = 同业 B2B 列表权限；办理 = channel_partners.partners.status_manage（老板 + 技术管理员 + 客户经理）。
 */
type Tab = "applications" | "awaiting" | "golive" | "done";
const fmt = (iso: string) => new Date(iso).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
const sub = "text-[12px] text-slate-400";

export function OpenApiApprovalsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canDecide = useCan("channel_partners.partners.status_manage");
  const [tab, setTab] = useState<Tab>("applications");
  const [review, setReview] = useState<OpenApiApplication | null>(null);
  const [issued, setIssued] = useState<{ name: string; key?: string; emailSent?: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();

  const counts = useQuery({ queryKey: ["open-api", "pending-counts"], queryFn: openApiAdmin.pendingCounts, staleTime: 15_000 });
  const apps = useQuery({ queryKey: ["open-api", "applications", "pending"], queryFn: () => openApiAdmin.applications("pending"), enabled: tab === "applications" });
  const awaiting = useQuery({ queryKey: ["open-api", "applications", "awaiting"], queryFn: () => openApiAdmin.applications("awaiting_partner_confirm"), enabled: tab === "awaiting" });
  const goLive = useQuery({ queryKey: ["open-api", "go-live", "pending"], queryFn: () => openApiAdmin.goLiveRequests("pending"), enabled: tab === "golive" });
  const doneApproved = useQuery({ queryKey: ["open-api", "applications", "approved"], queryFn: () => openApiAdmin.applications("approved"), enabled: tab === "done" });
  const doneRejected = useQuery({ queryKey: ["open-api", "applications", "rejected"], queryFn: () => openApiAdmin.applications("rejected"), enabled: tab === "done" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["open-api"] });

  const issue = useMutation({
    mutationFn: (g: { partner_id: string; partner_name: string }) => openApiAdmin.issueLiveKey(g.partner_id).then((r) => ({ name: g.partner_name, key: `${r.prefix}…${r.last4}`, emailSent: r.email_sent })),
    onSuccess: (r) => { setIssued(r); refresh(); },
    onError: (e) => setErr((e as Error).message || "发正式密钥失败"),
  });

  async function rejectGoLive(partnerId: string, name: string) {
    setErr(null);
    try {
      const r = await askReason({ scene: "openapi_go_live_reject", title: "退回上线申请", subtitle: `${name}（对方回到「测试中」，原因会显示在对方门户并邮件通知）`, required: true, danger: true, confirmText: "确认退回" });
      if (!r) return;
      await openApiAdmin.rejectGoLive(partnerId, r);
      refresh();
    } catch (e) { if (!isReasonCancelled(e)) setErr((e as Error).message || "退回失败"); }
  }

  const resend = useMutation({
    mutationFn: (id: string) => openApiAdmin.resendConfirm(id),
    onSuccess: () => { setErr(null); refresh(); },
    onError: (e) => setErr((e as Error).message || "重发确认失败"),
  });

  const Tabs = (
    <div className="flex gap-1.5 border-b border-slate-200">
      {([["applications", "接入申请", counts.data?.applications], ["awaiting", "等对方确认", counts.data?.awaiting_partner_confirm], ["golive", "上线申请", counts.data?.go_live], ["done", "已处理", 0]] as const).map(([k, label, n]) => (
        <button key={k} type="button" onClick={() => setTab(k)} data-testid={`oa-tab-${k}`}
          className={"px-3 py-2 text-[13px] font-medium border-b-2 transition " + (tab === k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-600 hover:text-slate-900")}>
          {label}{!!n && <span className="ml-1.5 rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">{n}</span>}
        </button>
      ))}
    </div>
  );

  return (
    <PageShell title="开放平台审批" subtitle="新同行接入申请 + 上线申请，都在这一页处理">
      {Tabs}
      {err && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

      {tab === "applications" && (
        apps.isLoading ? <LoadingState /> : apps.isError ? <QueryError what="接入申请" onRetry={() => apps.refetch()} /> : (apps.data?.items.length ?? 0) === 0 ? <EmptyState message="没有待审核的接入申请" /> : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                <tr><th className="px-3 py-2">提交时间</th><th className="px-3 py-2">公司</th><th className="px-3 py-2">网站 / 所在地</th><th className="px-3 py-2">预计月单量</th><th className="px-3 py-2">希望结算</th><th className="px-3 py-2">是否已是同行</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {apps.data!.items.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`oa-app-${a.id}`}>
                    <td className="px-3 py-2 tabular-nums">{fmt(a.submitted_at)}</td>
                    <td className="px-3 py-2"><b>{a.company_name}</b><div className={sub}>{a.contact_email}</div></td>
                    <td className="px-3 py-2">{a.website} · {a.country}</td>
                    <td className="px-3 py-2">{a.monthly_volume ?? "—"}</td>
                    <td className="px-3 py-2">{a.settlement_wish ?? "—"}</td>
                    <td className="px-3 py-2">{a.matched_partner ? <Pill tone="info">疑似已有：{a.matched_partner.code}</Pill> : "新同行"}</td>
                    <td className="px-3 py-2 text-right">
                      {canDecide && <button type="button" onClick={() => setReview(a)} data-testid={`oa-review-${a.id}`} className="rounded-lg bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700">审核</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t border-slate-100 px-3 py-2 text-[12px] text-slate-400">「是否已是同行」系统按公司名 / 邮箱域名比对现有同行档案；疑似已有时审核弹窗默认「绑到已有档案」，不重复建档。</div>
          </div>
        )
      )}

      {tab === "awaiting" && (
        awaiting.isLoading ? <LoadingState /> : awaiting.isError ? <QueryError what="等对方确认的申请" onRetry={() => awaiting.refetch()} /> : (awaiting.data?.items.length ?? 0) === 0 ? <EmptyState message="没有在等对方确认的申请" /> : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                <tr><th className="px-3 py-2">提交时间</th><th className="px-3 py-2">公司</th><th className="px-3 py-2">绑定的同行</th><th className="px-3 py-2">确认截止</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {awaiting.data!.items.map((a) => {
                  const expired = !!a.confirm_expires_at && new Date(a.confirm_expires_at).getTime() < Date.now();
                  return (
                    <tr key={a.id} className="border-t border-slate-100" data-testid={`oa-wait-${a.id}`}>
                      <td className="px-3 py-2 tabular-nums">{fmt(a.submitted_at)}</td>
                      <td className="px-3 py-2"><b>{a.company_name}</b><div className={sub}>{a.contact_email}</div></td>
                      <td className="px-3 py-2">{a.matched_partner ? `${a.matched_partner.code} ${a.matched_partner.name}` : "—"}</td>
                      <td className="px-3 py-2">{a.confirm_expires_at ? <Pill tone={expired ? "danger" : "info"}>{expired ? "已过期 " : ""}{fmt(a.confirm_expires_at)}</Pill> : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {canDecide && <button type="button" disabled={resend.isPending} onClick={() => resend.mutate(a.id)} data-testid={`oa-resend-${a.id}`} className="rounded-lg border border-blue-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-40">重发确认</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-slate-100 px-3 py-2 text-[12px] text-slate-400">绑到已有同行的申请，要该同行主账号点「同意」后才开通；对方没收到或已过期，可以「重发确认」（有效期重新算 7 天）。</div>
          </div>
        )
      )}

      {tab === "golive" && (
        goLive.isLoading ? <LoadingState /> : goLive.isError ? <QueryError what="上线申请" onRetry={() => goLive.refetch()} /> : (goLive.data?.items.length ?? 0) === 0 ? <EmptyState message="没有待处理的上线申请" /> : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                <tr><th className="px-3 py-2">申请时间</th><th className="px-3 py-2">同行</th><th className="px-3 py-2">上线检查</th><th className="px-3 py-2">测试期间</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {goLive.data!.items.map((g) => {
                  const all = g.checklist_done >= g.checklist_total;
                  return (
                    <tr key={g.id} className="border-t border-slate-100" data-testid={`oa-gl-${g.id}`}>
                      <td className="px-3 py-2 tabular-nums">{fmt(g.requested_at)}</td>
                      <td className="px-3 py-2">{g.partner_name}（{g.partner_code}）</td>
                      <td className="px-3 py-2"><Pill tone={all ? "success" : "warning"}>{g.checklist_done} / {g.checklist_total}{all ? " 全过" : ""}</Pill></td>
                      <td className="px-3 py-2">调用 {g.calls.toLocaleString("en-US")} 次 · 模拟单 {g.sandbox_bookings} 单 · 推送成功 {g.webhook_success_pct == null ? "—" : `${g.webhook_success_pct}%`}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button type="button" onClick={() => navigate(`/admin/channel-partners/${g.partner_id}`)} className="mr-2 rounded-lg border border-slate-300 px-3 py-1 text-[12px] font-medium text-slate-600">看详情</button>
                        {canDecide && <button type="button" onClick={() => rejectGoLive(g.partner_id, g.partner_name)} data-testid={`oa-gl-reject-${g.id}`} className="mr-2 rounded-lg border border-rose-300 px-3 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50">退回</button>}
                        {canDecide && <button type="button" disabled={!all || issue.isPending} onClick={() => issue.mutate({ partner_id: g.partner_id, partner_name: g.partner_name })} data-testid={`oa-issue-${g.id}`}
                          className="rounded-lg bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">发正式密钥</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "done" && (
        (doneApproved.isLoading || doneRejected.isLoading) ? <LoadingState /> : (doneApproved.isError || doneRejected.isError) ? <QueryError what="已处理的申请" onRetry={() => { void doneApproved.refetch(); void doneRejected.refetch(); }} /> : (doneApproved.data?.items.length ?? 0) + (doneRejected.data?.items.length ?? 0) === 0 ? <EmptyState message="还没有处理过的申请" /> : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[12px] text-slate-500"><tr><th className="px-3 py-2">处理时间</th><th className="px-3 py-2">公司</th><th className="px-3 py-2">结果</th><th className="px-3 py-2">处理人</th><th className="px-3 py-2">说明</th></tr></thead>
              <tbody>
                {[...(doneApproved.data?.items ?? []), ...(doneRejected.data?.items ?? [])]
                  .sort((a, b) => (b.decided_at ?? "").localeCompare(a.decided_at ?? ""))
                  .map((a) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 tabular-nums">{a.decided_at ? fmt(a.decided_at) : "—"}</td>
                      <td className="px-3 py-2"><b>{a.company_name}</b><div className={sub}>{a.contact_email}</div></td>
                      <td className="px-3 py-2"><Pill tone={a.status === "approved" ? "success" : "danger"}>{a.status === "approved" ? "已通过" : "已拒绝"}</Pill></td>
                      <td className="px-3 py-2">{a.decided_by_name ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-600">{a.reject_reason ?? ""}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {review && (
        <ApproveModal
          app={review}
          onClose={() => setReview(null)}
          onDone={() => { setReview(null); refresh(); }}
          onReject={async () => {
            try {
              const r = await askReason({ scene: "openapi_reject", title: "拒绝接入申请", subtitle: `${review.company_name}（原因会发给对方）`, required: true, danger: true, confirmText: "确认拒绝" });
              if (!r) return;
              await openApiAdmin.reject(review.id, r);
              setReview(null); refresh();
            } catch (e) { if (!isReasonCancelled(e)) setErr((e as Error).message || "拒绝失败"); }
          }}
        />
      )}
      {reasonDialog}
      {issued && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" role="dialog" aria-modal="true" data-testid="oa-issued">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
            <h2 className="mb-2 text-base font-semibold text-slate-900">正式密钥已发放 · {issued.name}</h2>
            <p className="mb-3 text-[13px] text-slate-600" data-testid="oa-issued-note">{issued.emailSent === true ? "已邮件通知对方。" : issued.emailSent === false ? "⚠ 通知邮件没有发出去（对方没有可用邮箱或发送失败），请自行联系对方。" : ""}后台不显示密钥明文：对方登录开放平台后，在「概览 · 密钥」点「领取」取走一次完整密钥（7 天内有效）。</p>
            {issued.key && <code className="mb-3 block break-all rounded-lg bg-slate-100 px-3 py-2 text-[12px]" data-testid="oa-issued-key">{issued.key}</code>}
            <div className="flex justify-end"><button type="button" onClick={() => setIssued(null)} className="rounded-lg bg-blue-600 px-4 py-1.5 text-[13px] font-semibold text-white">我已知晓</button></div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

/** 客群下拉里「不设客群」的取值（不是空串：空串 = 还没选） */
const NO_TIER = "__none__";
const MODES: SettlementMode[] = ["credit_monthly", "prepay", "credit_per_trip"];

/** 查询失败：不能当成「没有数据」（FIX-2 第 17 条） */
function QueryError({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div role="alert" data-testid="oa-load-error" className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
      <span>{what}加载失败，请重试。</span>
      <button type="button" onClick={onRetry} data-testid="oa-load-retry" className="rounded-lg border border-rose-300 bg-white px-3 py-1 text-[12px] font-medium text-rose-700 hover:bg-rose-100">重试</button>
    </div>
  );
}

function ApproveModal({ app, onClose, onDone, onReject }: { app: OpenApiApplication; onClose: () => void; onDone: () => void; onReject: () => void }) {
  const tiers = useQuery({ queryKey: ["b2b", "tiers"], queryFn: () => partnersApi.listTiers(), staleTime: 60_000 });
  // 绑定方式：matched = 系统比对出的那家；pick = 在全部同行档案里搜索挑一家；new = 新建
  const [bind, setBind] = useState<"matched" | "pick" | "new">(app.matched_partner ? "matched" : "new");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  const partners = useQuery({ queryKey: ["b2b", "partners", "all-for-bind"], queryFn: () => partnersApi.list(), staleTime: 60_000, enabled: bind === "pick" });
  const needle = q.trim().toLowerCase();
  const pickList = (partners.data ?? []).filter((p) => !needle || p.name.toLowerCase().includes(needle) || (p.contact_email ?? "").toLowerCase().includes(needle)).slice(0, 50);
  const [mode, setMode] = useState<SettlementMode>(app.settlement_wish === "预付" ? "prepay" : "credit_monthly");
  const [credit, setCredit] = useState("");   // 授信额度不预填：留空必填（不拿默认值冒充事实）
  const [tier, setTier] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const tierList = (tiers.data ?? []).filter((t) => t.is_active);
  // 客群必选，不默认任何一项；「不设客群」是一个明确的选项（客群表为空时也能批：按同行基础价、无加成，传给后端 tier_code=null）
  const tierCode = tier === NO_TIER ? "" : tier;
  const tierPicked = tier !== "";
  const dollars = Number(credit);
  // 绑到已有同行：后端不改它原来的结算 / 授信 / 客群（授信变更要走授信变更审批，不能从这里绕过去）→ 这三项只对「新建」有意义
  const boundId = bind === "matched" ? app.matched_partner?.id : bind === "pick" ? picked : undefined;
  const bound = bind !== "new";
  const bad = bound || mode === "prepay" ? false : !(dollars >= 0) || credit.trim() === "";
  const needPick = bind === "pick" && !picked;

  const approve = useMutation({
    mutationFn: () => openApiAdmin.approve(app.id, {
      ...(boundId ? { partner_id: boundId } : {}),
      settlement_mode: mode, credit_limit_cents: bound || mode === "prepay" ? 0 : Math.round(dollars * 100), ...(bound || !tierCode ? {} : { tier_code: tierCode }),
    }),
    onSuccess: onDone,
    onError: (e) => setErr((e as Error).message || "通过失败，请重试"),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" role="dialog" aria-modal="true" data-testid="oa-approve-modal">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
        <h2 className="mb-3 text-base font-semibold text-slate-900">审核接入申请 · {app.company_name}</h2>
        <div className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-slate-50 p-3 text-[12px] text-slate-600">
          <span>网站：{app.website}</span><span>所在地：{app.country}</span>
          <span>业务联系人：{app.contact_name}（{app.contact_email}）</span><span>技术联系人：{app.tech_email}</span>
          <span>预计月单量：{app.monthly_volume ?? "—"}</span><span>希望结算：{app.settlement_wish ?? "—"}</span>
        </div>
        <div className="grid grid-cols-[90px_minmax(0,1fr)] items-start gap-x-3 gap-y-3 text-[13px]">
          <span className="pt-1 text-slate-500">同行档案</span>
          <div className="flex min-w-0 flex-col gap-1">
            {app.matched_partner && <label className="flex items-center gap-2"><input type="radio" checked={bind === "matched"} onChange={() => setBind("matched")} data-testid="oa-bind-existing" /> 绑到已有：{app.matched_partner.code} {app.matched_partner.name}</label>}
            <label className="flex items-center gap-2"><input type="radio" checked={bind === "pick"} onChange={() => setBind("pick")} data-testid="oa-bind-pick" /> 绑到其它已有同行（搜索选择）</label>
            {bind === "pick" && (
              <div className="ml-6 flex min-w-0 flex-col gap-1" data-testid="oa-pick-box">
                <input className="h-8 w-full min-w-0 rounded-lg border border-slate-300 px-2" placeholder="按公司名 / 联系邮箱搜索" aria-label="搜索同行档案" value={q} onChange={(e) => setQ(e.target.value)} data-testid="oa-pick-search" />
                <select className="h-9 w-full min-w-0 rounded-lg border border-slate-300 px-2" aria-label="选择同行档案" value={picked} onChange={(e) => setPicked(e.target.value)} data-testid="oa-pick-select">
                  <option value="">{partners.isLoading ? "加载中…" : partners.isError ? "同行档案加载失败" : `请选择（${pickList.length} 家）`}</option>
                  {pickList.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.contact_email}</option>)}
                </select>
              </div>
            )}
            <label className="flex items-center gap-2"><input type="radio" checked={bind === "new"} onChange={() => setBind("new")} data-testid="oa-bind-new" /> 新建同行档案</label>
          </div>
          {app.match_warning && <div role="alert" className="col-span-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-800" data-testid="oa-match-warning">⚠ {app.match_warning}</div>}
          {bound && <div className="col-span-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-700" data-testid="oa-bound-hint">绑到已有同行时，它原来的结算方式 / 授信额度 / 客群保持不变；要调整请到该同行详情里改（授信变更走审批）。</div>}
          <span className="pt-1 text-slate-500">结算方式</span>
          <select className="h-9 w-full min-w-0 rounded-lg border border-slate-300 px-2 disabled:bg-slate-100" disabled={bound} value={mode} onChange={(e) => setMode(e.target.value as SettlementMode)} data-testid="oa-mode">
            {MODES.map((m) => <option key={m} value={m}>{SETTLEMENT_MODE_LABEL[m]}</option>)}
          </select>
          <span className="pt-1 text-slate-500">授信额度</span>
          <div className="flex items-center gap-2"><span>$</span><input className="h-9 w-40 rounded-lg border border-slate-300 px-2 disabled:bg-slate-100" placeholder={bound ? "保持原值" : "必填"} inputMode="decimal" value={mode === "prepay" ? "0" : bound ? "" : credit} disabled={mode === "prepay" || bound} onChange={(e) => setCredit(e.target.value)} data-testid="oa-credit" />{mode !== "prepay" && !bound && credit.trim() !== "" && dollars >= 0 && <span className="text-[12px] text-slate-400">{formatCents(Math.round(dollars * 100))}</span>}</div>
          <span className="pt-1 text-slate-500">客群</span>
          <select className="h-9 w-full min-w-0 rounded-lg border border-slate-300 px-2 disabled:bg-slate-100" disabled={bound} value={tier} onChange={(e) => setTier(e.target.value)} data-testid="oa-tier">
            <option value="">请选择客群</option>
            <option value={NO_TIER}>不设客群（按同行基础价，无加成）</option>
            {tierList.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
        </div>
        <div className="mt-3 text-[12px] text-slate-400">通过后：建 / 绑档案 → 主账号（已有就不建）+ 技术对接账号（申请里的技术邮箱）→ 发测试密钥 → 邮件通知对方。客户专属价以后在同行详情里设。</div>
        {err && <div role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{err}</div>}
        <div className="mt-4 flex items-center justify-between">
          <button type="button" onClick={onReject} data-testid="oa-reject" className="rounded-lg border border-rose-300 px-4 py-1.5 text-[13px] font-medium text-rose-600 hover:bg-rose-50">拒绝（填原因）</button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-1.5 text-[13px] font-medium text-slate-600">取消</button>
            <button type="button" disabled={bad || needPick || (!bound && !tierPicked) || approve.isPending} onClick={() => approve.mutate()} data-testid="oa-approve" className="rounded-lg bg-blue-600 px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">通过</button>
          </div>
        </div>
      </div>
    </div>
  );
}
